// src/pages/Dashboard.jsx
import { useState, useEffect, useMemo } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import UploadModal from "../components/UploadModal";
import EditModal from "../components/EditModal";
import DeleteModal from "../components/DeleteModal";
import Toast from "../components/Toast";

const STORAGE_LIMIT_BYTES = 4.8 * 1024 * 1024 * 1024; // 4.8 GB

function formatBytes(bytes) {
  if (bytes === 0) return "0 MB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function Dashboard() {
  const { logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [deletePost, setDeletePost] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    const q = query(collection(db, "posts"), orderBy("diagDate", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Storage usage calculation ──────────────────────────
  const storageUsedBytes = useMemo(() => {
    return posts.reduce((sum, post) => {
      return sum + (post.files || []).reduce((s, f) => s + (f.size || 0), 0);
    }, 0);
  }, [posts]);

  const storagePercent = Math.min((storageUsedBytes / STORAGE_LIMIT_BYTES) * 100, 100);
  const isStorageWarning = storagePercent >= 80;
  const isStorageCritical = storagePercent >= 95;
  const storageBarColor = isStorageCritical
    ? "linear-gradient(90deg, #ef4444, #b91c1c)"
    : isStorageWarning
    ? "linear-gradient(90deg, #f97316, #ea580c)"
    : "linear-gradient(90deg, #1e6fd9, #60a5fa)";

  // ── Filter ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return posts;
    return posts.filter(
      (p) =>
        p.title?.toLowerCase().includes(kw) ||
        p.client?.toLowerCase().includes(kw) ||
        p.diagDate?.includes(kw) ||
        p.capacity?.toLowerCase().includes(kw)
    );
  }, [posts, search]);

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`).then(() => {
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
      showToast("링크가 클립보드에 복사되었습니다.", "info");
    });
  };

  return (
    <div style={S.root}>
      {/* ── Nav ── */}
      <nav style={S.nav}>
        <div style={S.navBrand}>
          <div style={S.navLogo}>DS</div>
          <div>
            <div style={S.navTitle}>DS ENGINEERING</div>
            <div style={S.navSub}>고객사 파일 저장소</div>
          </div>
        </div>
        <button style={S.logoutBtn} onClick={logout}>로그아웃</button>
      </nav>

      {/* ── Main ── */}
      <main style={S.main}>
        <div style={S.boardWrap}>

          {/* Header row */}
          <div style={S.boardHeader}>
            <div style={S.boardTitleWrap}>
              <div style={S.boardAccent} />
              <h1 style={S.boardTitle}>진단 고객사</h1>
              <span style={S.postCount}>{posts.length}건</span>
            </div>
            {/* 등록 버튼: 파란색 */}
            <button
              style={{
                ...S.registerBtn,
                opacity: storagePercent >= 100 ? 0.5 : 1,
                cursor: storagePercent >= 100 ? "not-allowed" : "pointer",
              }}
              onClick={() => storagePercent < 100 && setShowUpload(true)}
              title={storagePercent >= 100 ? "저장 공간이 가득 찼습니다." : "새 게시물 등록"}
            >
              + 등록
            </button>
          </div>

          {/* ── Storage meter ── */}
          <div style={S.storageMeter}>
            <div style={S.storageTop}>
              <div style={S.storageLabel}>
                <span style={S.storageLabelText}>
                  {isStorageCritical ? "🔴" : isStorageWarning ? "🟠" : "🟢"} 스토리지 사용량
                </span>
                <span
                  style={{
                    ...S.storagePct,
                    color: isStorageCritical ? "#dc2626" : isStorageWarning ? "#ea580c" : "#1e6fd9",
                  }}
                >
                  {storagePercent.toFixed(1)}%
                </span>
              </div>
              <div style={S.storageSizeText}>
                {formatBytes(storageUsedBytes)}{" "}
                <span style={S.storageSizeDim}>/ 4.8 GB</span>
                {isStorageCritical && (
                  <span style={S.storageAlert}>⚠ 용량 한도 초과 임박</span>
                )}
                {!isStorageCritical && isStorageWarning && (
                  <span style={{ ...S.storageAlert, color: "#ea580c", background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.3)" }}>
                    주의: 80% 이상 사용 중
                  </span>
                )}
              </div>
            </div>
            <div style={S.storageTrack}>
              <div
                style={{
                  ...S.storageFill,
                  width: `${storagePercent}%`,
                  background: storageBarColor,
                }}
              />
            </div>
          </div>

          {/* Search */}
          <div style={S.searchWrap}>
            <span style={S.searchIcon}>🔍</span>
            <input
              style={S.searchInput}
              type="text"
              placeholder="고객사명, 날짜, 용량으로 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button style={S.clearBtn} onClick={() => setSearch("")}>✕</button>
            )}
          </div>
          {search && (
            <div style={S.searchHint}>"{search}" — {filtered.length}건</div>
          )}

          {/* Column labels */}
          {!loading && filtered.length > 0 && (
            <div style={S.tableHeader}>
              <span style={S.thIdx}>#</span>
              <span style={{ flex: 1 }}>게시물 제목</span>
              <span style={S.thDate}>진단일</span>
              <span style={S.thActions}>관리</span>
            </div>
          )}

          {/* List */}
          <div style={S.postList}>
            {loading ? (
              <div style={S.centerState}>
                <div style={S.spinner} />
                <span style={{ color: "#6b7280", fontSize: "14px" }}>불러오는 중...</span>
              </div>
            ) : posts.length === 0 ? (
              <div style={S.centerState}>
                <div style={{ fontSize: "40px", marginBottom: "14px" }}>📋</div>
                <div style={S.emptyMsg}>게시물이 없습니다.</div>
                <div style={S.emptySub}>등록 버튼을 눌러 첫 게시물을 추가하세요.</div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={S.centerState}>
                <div style={{ fontSize: "32px", marginBottom: "10px" }}>🔍</div>
                <div style={S.emptyMsg}>검색 결과가 없습니다.</div>
                <div style={S.emptySub}>다른 검색어를 사용해보세요.</div>
              </div>
            ) : (
              filtered.map((post) => {
                const isOpen = expandedId === post.id;
                const sortedFiles = (post.files || []).sort((a, b) => a.order - b.order);
                const rank = posts.length - posts.indexOf(post);
                return (
                  <div key={post.id} style={S.postWrap}>
                    <div
                      style={{
                        ...S.postRow,
                        background: isOpen ? "#fef9c3" : "#fffbeb",
                      }}
                      onClick={() => setExpandedId(isOpen ? null : post.id)}
                    >
                      <span style={S.colIdx}>{String(rank).padStart(2, "0")}</span>
                      <span style={S.colTitle}>
                        <span style={S.postTitle}>{post.title}</span>
                        <span style={S.filesBadge}>{sortedFiles.length}개</span>
                        <span style={S.chevron}>{isOpen ? "▲" : "▼"}</span>
                      </span>
                      <span style={S.colDate}>{post.diagDate}</span>
                      <span style={S.colActions} onClick={(e) => e.stopPropagation()}>
                        {/* 수정 버튼: 주황색 */}
                        <button style={S.btnEdit} onClick={() => setEditPost(post)}>수정</button>
                        {/* 삭제 버튼: 붉은색 */}
                        <button style={S.btnDelete} onClick={() => setDeletePost(post)}>삭제</button>
                      </span>
                    </div>

                    {isOpen && (
                      <div style={S.expandBody}>
                        <div style={S.metaRow}>
                          <span style={S.chip}>📅 {post.diagDate}</span>
                          <span style={S.chip}>🏢 {post.client}</span>
                          <span style={S.chip}>⚡ {post.capacity}</span>
                        </div>
                        <div style={S.linkRow}>
                          <span style={S.linkLabel}>공유 링크</span>
                          <code style={S.slugCode}>{window.location.origin}/{post.slug}</code>
                          <button
                            style={{
                              ...S.copyBtn,
                              background: copiedSlug === post.slug ? "#dcfce7" : "#f9fafb",
                              color: copiedSlug === post.slug ? "#16a34a" : "#6b7280",
                              borderColor: copiedSlug === post.slug ? "#16a34a" : "#d1d5db",
                            }}
                            onClick={() => copyLink(post.slug)}
                          >
                            {copiedSlug === post.slug ? "✓ 복사됨" : "링크 복사"}
                          </button>
                          <a href={`/${post.slug}`} target="_blank" rel="noreferrer" style={S.openLink}>새 탭 ↗</a>
                        </div>
                        <div style={S.fileListTitle}>첨부 파일 ({sortedFiles.length}개)</div>
                        <div style={S.fileGrid}>
                          {sortedFiles.map((file, fi) => (
                            <div key={fi} style={S.fileCard}>
                              <div style={S.fileCardLeft}>
                                <span style={S.fileCardNum}>{fi + 1}</span>
                                <span style={S.fileCardName}>📄 {file.name}</span>
                                {file.size > 0 && (
                                  <span style={S.fileCardSize}>{formatBytes(file.size)}</span>
                                )}
                              </div>
                              <a href={file.url} target="_blank" rel="noreferrer" style={S.fileCardDown} download>↓ 다운로드</a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>

      {showUpload && (
        <UploadModal
          storageUsedBytes={storageUsedBytes}
          storageLimitBytes={STORAGE_LIMIT_BYTES}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); showToast("게시물이 등록되었습니다."); }}
        />
      )}
      {editPost && (
        <EditModal
          post={editPost}
          onClose={() => setEditPost(null)}
          onSuccess={() => { setEditPost(null); showToast("게시물이 수정되었습니다."); }}
        />
      )}
      {deletePost && (
        <DeleteModal
          post={deletePost}
          onClose={() => setDeletePost(null)}
          onSuccess={() => { setDeletePost(null); showToast("게시물이 삭제되었습니다.", "error"); }}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

const S = {
  root: { minHeight: "100vh", background: "#ffffff", fontFamily: "'Pretendard','Noto Sans KR',sans-serif" },
  nav: { height: "64px", background: "#fffbeb", borderBottom: "1px solid #fde68a", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 clamp(16px,4vw,40px)", position: "sticky", top: 0, zIndex: 100 },
  navBrand: { display: "flex", alignItems: "center", gap: "12px" },
  navLogo: { width: "36px", height: "36px", background: "linear-gradient(135deg,#1e6fd9,#0d4fa8)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "800", fontSize: "13px", flexShrink: 0 },
  navTitle: { color: "#111827", fontSize: "15px", fontWeight: "700" },
  navSub: { color: "#6b7280", fontSize: "11px" },
  logoutBtn: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", padding: "7px 16px", color: "#6b7280", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  main: { paddingTop: "calc(20vh - 64px)", paddingBottom: "80px", paddingLeft: "clamp(16px,4vw,40px)", paddingRight: "clamp(16px,4vw,40px)" },
  boardWrap: { maxWidth: "980px", margin: "0 auto" },
  boardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" },
  boardTitleWrap: { display: "flex", alignItems: "center", gap: "12px" },
  boardAccent: { width: "4px", height: "28px", background: "linear-gradient(180deg,#1e6fd9,#0d4fa8)", borderRadius: "2px", flexShrink: 0 },
  boardTitle: { color: "#111827", fontSize: "clamp(18px,3vw,22px)", fontWeight: "800", margin: 0, letterSpacing: "-0.02em" },
  postCount: { background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "20px", padding: "2px 10px", color: "#6b7280", fontSize: "12px" },
  registerBtn: { background: "linear-gradient(135deg,#1e6fd9,#0d4fa8)", border: "none", borderRadius: "8px", padding: "10px 22px", color: "#fff", fontSize: "13px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(30,111,217,0.3)", whiteSpace: "nowrap", transition: "opacity 0.2s" },

  // ── Storage meter styles ─────────────────────────────
  storageMeter: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "10px",
    padding: "14px 18px",
    marginBottom: "16px",
  },
  storageTop: { marginBottom: "10px" },
  storageLabel: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" },
  storageLabelText: { color: "#374151", fontSize: "12px", fontWeight: "600" },
  storagePct: { fontSize: "13px", fontWeight: "700" },
  storageSizeText: { display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "12px" },
  storageSizeDim: { color: "#9ca3af" },
  storageAlert: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.3)",
    borderRadius: "4px",
    padding: "1px 8px",
    color: "#dc2626",
    fontSize: "11px",
    fontWeight: "600",
  },
  storageTrack: { height: "6px", background: "#e5e7eb", borderRadius: "3px", overflow: "hidden" },
  storageFill: { height: "100%", borderRadius: "3px", transition: "width 0.6s ease" },

  searchWrap: { display: "flex", alignItems: "center", gap: "10px", background: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "0 14px", marginBottom: "6px" },
  searchIcon: { fontSize: "14px", flexShrink: 0 },
  searchInput: { flex: 1, background: "none", border: "none", outline: "none", color: "#111827", fontSize: "14px", padding: "12px 0", fontFamily: "inherit" },
  clearBtn: { background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "13px", padding: "4px", flexShrink: 0 },
  searchHint: { color: "#6b7280", fontSize: "12px", marginBottom: "10px" },
  tableHeader: { display: "flex", alignItems: "center", padding: "9px 16px 9px 19px", background: "#fef3c7", border: "1px solid #fde68a", borderBottom: "none", borderRadius: "8px 8px 0 0", color: "#9ca3af", fontSize: "11px", fontWeight: "600", letterSpacing: "0.07em", textTransform: "uppercase" },
  thIdx: { width: "36px", flexShrink: 0 },
  thDate: { width: "110px", flexShrink: 0 },
  thActions: { width: "116px", flexShrink: 0, textAlign: "right" },
  postList: { border: "1px solid #fde68a", borderRadius: "8px", overflow: "hidden" },
  centerState: { padding: "56px 20px", textAlign: "center", background: "#fffbeb", display: "flex", flexDirection: "column", alignItems: "center" },
  emptyMsg: { color: "#374151", fontSize: "15px", fontWeight: "600", marginBottom: "6px" },
  emptySub: { color: "#9ca3af", fontSize: "13px" },
  spinner: { width: "20px", height: "20px", border: "2px solid #e5e7eb", borderTopColor: "#1e6fd9", borderRadius: "50%", animation: "dspin 0.8s linear infinite", marginBottom: "10px" },
  postWrap: { borderBottom: "1px solid #fef3c7", background: "#fffbeb" },
  postRow: { display: "flex", alignItems: "center", padding: "13px 16px", cursor: "pointer", transition: "background 0.15s", gap: "8px" },
  colIdx: { width: "36px", color: "#9ca3af", fontSize: "12px", fontFamily: "monospace", flexShrink: 0 },
  colTitle: { flex: 1, display: "flex", alignItems: "center", gap: "8px", overflow: "hidden", minWidth: 0 },
  postTitle: { color: "#111827", fontSize: "13px", fontWeight: "500", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  filesBadge: { background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: "4px", padding: "1px 6px", color: "#1d4ed8", fontSize: "10px", fontWeight: "700", flexShrink: 0 },
  chevron: { color: "#9ca3af", fontSize: "9px", flexShrink: 0 },
  colDate: { width: "110px", color: "#6b7280", fontSize: "12px", fontFamily: "monospace", flexShrink: 0 },
  colActions: { width: "116px", display: "flex", gap: "6px", justifyContent: "flex-end", flexShrink: 0 },
  btnEdit: { background: "linear-gradient(135deg,#f97316,#ea580c)", border: "none", borderRadius: "5px", padding: "5px 10px", color: "#fff", fontSize: "12px", cursor: "pointer", fontFamily: "inherit", fontWeight: "600" },
  btnDelete: { background: "linear-gradient(135deg,#ef4444,#dc2626)", border: "none", borderRadius: "5px", padding: "5px 10px", color: "#fff", fontSize: "12px", cursor: "pointer", fontFamily: "inherit", fontWeight: "600" },
  expandBody: { background: "#ffffff", padding: "16px 20px 20px", borderTop: "1px solid #fde68a" },
  metaRow: { display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" },
  chip: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "20px", padding: "4px 12px", color: "#374151", fontSize: "12px" },
  linkRow: { display: "flex", alignItems: "center", gap: "10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", flexWrap: "wrap" },
  linkLabel: { color: "#9ca3af", fontSize: "11px", fontWeight: "600", letterSpacing: "0.05em", textTransform: "uppercase", flexShrink: 0 },
  slugCode: { color: "#1d4ed8", fontSize: "12px", fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: "60px" },
  copyBtn: { border: "1px solid", borderRadius: "5px", padding: "4px 12px", fontSize: "11px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit", flexShrink: 0, transition: "all 0.2s" },
  openLink: { color: "#6b7280", fontSize: "11px", textDecoration: "none", flexShrink: 0 },
  fileListTitle: { color: "#9ca3af", fontSize: "11px", fontWeight: "600", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" },
  fileGrid: { display: "flex", flexDirection: "column", gap: "6px" },
  fileCard: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", padding: "10px 14px", gap: "12px" },
  fileCardLeft: { display: "flex", alignItems: "center", gap: "10px", overflow: "hidden", flex: 1 },
  fileCardNum: { color: "#9ca3af", fontSize: "12px", minWidth: "18px", textAlign: "center", flexShrink: 0 },
  fileCardName: { color: "#111827", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileCardSize: { color: "#9ca3af", fontSize: "11px", flexShrink: 0 },
  fileCardDown: { color: "#1d4ed8", fontSize: "12px", fontWeight: "700", textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 },
};
